import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebounce } from "@/hooks/useDebounce";
import { usePartnerRequests, usePartnerStats } from "@/hooks/usePartnerProgram";
import { PartnerRequestStatus } from "@/lib/api/partnerProgramService";
import { format } from "date-fns";
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  Link as LinkIcon,
  Mail,
  Search,
  User,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

function StatusBadge({ status }: { status: string }) {
  const variants: Record<
    string,
    "default" | "secondary" | "outline" | "destructive"
  > = {
    ACCEPTED: "default",
    REJECTED: "destructive",
    PENDING: "outline",
    UNDER_REVIEW: "secondary",
    NEEDS_REVISION: "outline",
  };

  return (
    <Badge
      variant={variants[status] || "outline"}
      data-testid={`badge-status-${status}`}
    >
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return format(date, "MMM d, yyyy");
}

const REGION_DISPLAY = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();

function countryName(code: string | null | undefined): string {
  if (!code) return "";
  const trimmed = code.trim();
  if (!trimmed) return "";
  if (/^[A-Za-z]{2}$/.test(trimmed) && REGION_DISPLAY) {
    try {
      return REGION_DISPLAY.of(trimmed.toUpperCase()) ?? trimmed;
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

export default function ReviewPage() {
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState<PartnerRequestStatus>("PENDING");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const debouncedSearch = useDebounce(search, 300);

  const { data: statsData } = usePartnerStats();
  const { data: requestsData, isLoading } = usePartnerRequests({
    page: currentPage,
    limit: pageSize,
    status: activeTab,
    search: debouncedSearch,
  });

  const items = requestsData?.items || [];
  const totalItems = requestsData?.total || 0;
  const totalPages = requestsData?.totalPages || 0;

  const handleTabChange = (value: string) => {
    setActiveTab(value as PartnerRequestStatus);
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="review-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation("/affiliates")}
          data-testid="button-back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-page-title"
          >
            Partner Applications
          </h1>
          <p className="text-sm text-muted-foreground">
            Review and manage affiliate partner program applications
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full md:w-auto"
        >
          <TabsList data-testid="tabs-review">
            <TabsTrigger value="PENDING" data-testid="tab-review-pending">
              Pending ({statsData?.PENDING || 0})
            </TabsTrigger>
            <TabsTrigger value="UNDER_REVIEW" data-testid="tab-review-review">
              In Review ({statsData?.UNDER_REVIEW || 0})
            </TabsTrigger>
            <TabsTrigger
              value="NEEDS_REVISION"
              data-testid="tab-review-revision"
            >
              Revision ({statsData?.NEEDS_REVISION || 0})
            </TabsTrigger>
            <TabsTrigger value="ACCEPTED" data-testid="tab-review-approved">
              Approved ({statsData?.ACCEPTED || 0})
            </TabsTrigger>
            <TabsTrigger value="REJECTED" data-testid="tab-review-rejected">
              Rejected ({statsData?.REJECTED || 0})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative w-full md:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search applicants..."
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="text-sm text-muted-foreground ml-auto tabular-nums"
              data-testid="text-item-count"
            >
              {totalItems} application{totalItems !== 1 ? "s" : ""} found
            </span>
          </div>

          <div className="border rounded-md overflow-auto bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">
                    Applied
                  </TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-muted-foreground py-8"
                    >
                      No applications in this queue
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow
                      key={item.id}
                      data-testid={`row-review-${item.id}`}
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {[item.firstName, item.lastName]
                              .filter(Boolean)
                              .join(" ") || "—"}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <User className="h-3 w-3" />{" "}
                            {item.user.name || "N/A"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm truncate max-w-[150px]">
                            {item.email || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Globe className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">
                            {countryName(item.country) || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.website ? (
                          <a
                            href={item.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <LinkIcon className="h-3 w-3" />
                            Visit
                          </a>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">
                            None
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={item.status} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground whitespace-nowrap">
                        {formatRelativeTime(item.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() =>
                            setLocation(`/affiliates/requests/${item.id}`)
                          }
                          data-testid={`button-open-${item.id}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoNext={currentPage < totalPages}
              canGoPrev={currentPage > 1}
              onPageChange={setCurrentPage}
              onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
            />
          </div>
        </>
      )}
    </div>
  );
}
